import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserDataContext } from "../components/UserDataContext";
import { Container } from "react-bootstrap";

import {
  Alert,
  Button,
  CircularProgress,
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Stack,
} from "@mui/material";

import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { apiRequest } from "../services/apiService";
import Alerts from "../components/Alerts";

export default function Qualifications() {
  const navigate = useNavigate();
  const { userData } = useContext(UserDataContext);

  const [experienceList, setExperienceList] = useState([]);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [loadingData, setLoadingData] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const [apiResponse, setApiResponse] = useState({});
  const [showApiResponse, setShowApiResponse] = useState(false);

  // Walidacja uprawnień
  useEffect(() => {
    if (userData && Number(userData.group) !== 3) {
      navigate("/nopermission");
    }
  }, [userData, navigate]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await apiRequest("getadds", "GET");
        if (Array.isArray(response)) {
          setExperienceList(response);
        }
      } catch (error) {
        console.error("Błąd pobierania listy:", error);
      }
    };

    if (userData?.userId) fetchData();
  }, [userData]);

  // Pobieranie aktualnie zapisanych wyborów użytkownika
  useEffect(() => {
    const fetchUserExperience = async () => {
      if (!userData?.userId) return;

      setLoadingData(true);
      try {
        const response = await apiRequest(
          `get-user-adds/${userData.userId}`,
          "GET",
        );

        if (response.success && Array.isArray(response.selectedIds)) {
          setSelectedItems(new Set(response.selectedIds.map(Number)));
        }
      } catch (error) {
        console.error("Błąd pobierania wyborów użytkownika:", error);
      } finally {
        setLoadingData(false);
      }
    };

    fetchUserExperience();
  }, [userData]);

  const toggleItem = (id) => {
    setSelectedItems((prev) => {
      const newSet = new Set(prev);
      newSet.has(id) ? newSet.delete(id) : newSet.add(id);
      return newSet;
    });
  };

  const handleSave = async () => {
    setIsSaving(true);
    setShowApiResponse(false);

    try {
      const payload = {
        userId: userData.userId,
        selectedIds: Array.from(selectedItems),
      };

      const response = await apiRequest("save-adds", "POST", payload);

      setApiResponse(
        response.success
          ? {
              title: "Sukces",
              severity: "success",
              info: "Kwalifikacje zapisane.",
            }
          : {
              title: "Błąd",
              severity: "warning",
              info: "Nie udało się zapisać zmian.",
            },
      );

      setShowApiResponse(true);
    } catch (error) {
      setApiResponse({
        title: "Błąd połączenia",
        severity: "error",
        info: "Wystąpił problem podczas komunikacji z serwerem.",
      });
      setShowApiResponse(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Container style={{ marginTop: "40px", marginBottom: "40px" }}>
      {showApiResponse && <Alerts data={apiResponse} />}

      <Typography variant="h4" mb={1}>
        Moje kwalifikacje
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        Zaznacz posiadane kwalifikacje i naciśnij{" "}
        <strong>"Zapisz zmiany"</strong>:
      </Alert>

      <Card>
        <CardContent>
          {loadingData ? (
            <Box textAlign="center" py={6}>
              <CircularProgress />
              <Typography mt={2}>Wczytywanie danych...</Typography>
            </Box>
          ) : experienceList.length > 0 ? (
            <>
              <Grid container spacing={2}>
                {experienceList.map((item) => {
                  const selected = selectedItems.has(item.id);

                  return (
                    <Grid item xs={12} sm={6} md={4} key={item.id}>
                      <Card
                        onClick={() => toggleItem(item.id)}
                        sx={{
                          cursor: "pointer",
                          border: selected
                            ? "2px solid #1976d2"
                            : "1px solid #e0e0e0",
                          backgroundColor: selected
                            ? "rgba(46,125,50,0.05)"
                            : "white",
                          transition: "all 0.2s ease-in-out",
                          "&:hover": {
                            borderColor: selected ? "#1976d2" : "#bdbdbd",
                            boxShadow: 2,
                          },
                        }}
                      >
                        <CardContent
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 2,
                            py: "12px !important", // Zmniejszony padding dla lepszego wyglądu tabelarycznego
                          }}
                        >
                          <CheckCircleIcon
                            color={selected ? "primary" : "disabled"}
                          />
                          <Typography fontWeight={selected ? 500 : 400}>
                            {item.description}
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              <Box mt={5} borderTop="1px solid #eee" pt={3}>
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    onClick={handleSave}
                    disabled={isSaving}
                    sx={{ px: 4 }}
                  >
                    {isSaving ? (
                      <CircularProgress size={24} color="inherit" />
                    ) : (
                      "Zapisz zmiany"
                    )}
                  </Button>
                </Stack>
              </Box>
            </>
          ) : (
            <Typography textAlign="center" color="text.secondary">
              Brak dostępnych pozycji w bazie danych.
            </Typography>
          )}
        </CardContent>
      </Card>
    </Container>
  );
}
